import { CONTRACTOR_ROLE_VALUES, TEAM_ROLES, USER_ROLES } from './constants';
import { getUserFromToken } from './permissions';

const TEAM_ROLE_VALUES = new Set(Object.values(TEAM_ROLES));
const CONTRACTOR_ROLE_VALUE_SET = new Set<string>(CONTRACTOR_ROLE_VALUES);

export function isContractorRole(role: string | undefined | null): boolean {
  return role != null && CONTRACTOR_ROLE_VALUE_SET.has(role);
}

export function rolesIncludeContractor(roles?: readonly string[] | null): boolean {
  return (roles ?? []).some(isContractorRole);
}

export function isIndividualContractorLikeRole(role: string): boolean {
  return role === USER_ROLES.INDIVIDUAL_CONTRACTOR || role === USER_ROLES.CONTRACTOR_OF_RECORD;
}

export function filterOutTeamRoles(roles: string[]): string[] {
  return roles.filter((role) => !TEAM_ROLE_VALUES.has(role));
}

export async function hasRole(roleName: string): Promise<boolean> {
  try {
    const user = await getUserFromToken();

    if (!user?.roles) {
      return false;
    }

    return user.roles.includes(roleName);
  } catch (error) {
    console.error('Error checking role:', error);
    return false;
  }
}

export async function hasAnyRole(roleNames: string[]): Promise<boolean> {
  try {
    const user = await getUserFromToken();

    if (!user?.roles) {
      return false;
    }

    return roleNames.some((roleName) => user.roles.includes(roleName));
  } catch (error) {
    console.error('Error checking roles:', error);
    return false;
  }
}

export async function hasAllRoles(roleNames: string[]): Promise<boolean> {
  try {
    const user = await getUserFromToken();

    if (!user?.roles) {
      return false;
    }

    return roleNames.every((roleName) => user.roles.includes(roleName));
  } catch (error) {
    console.error('Error checking roles:', error);
    return false;
  }
}

export async function getUserRoles(): Promise<string[]> {
  try {
    const user = await getUserFromToken();
    return user?.roles || [];
  } catch (error) {
    console.error('Error getting user roles:', error);
    return [];
  }
}

export async function hasTeamRole(roleName: string): Promise<boolean> {
  try {
    const user = await getUserFromToken();
    const firstTeam = user?.teams?.[0];

    if (!firstTeam?.team_role) {
      return false;
    }
    return firstTeam.team_role.includes(roleName);
  } catch (error) {
    console.error('Error checking team role:', error);
    return false;
  }
}

export async function getTeamLeadId(): Promise<number | undefined> {
  try {
    const user = await getUserFromToken();
    const firstTeam = user?.teams?.[0];

    return firstTeam?.team_id;
  } catch (error) {
    console.error('Error getting team lead id:', error);
    return undefined;
  }
}
